import { useAppContext } from '../../contexts/AppContext';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Server } from '../../services/Server';
import { useQuery } from '@tanstack/react-query';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import CachedResultBadge from '../common/CachedResultBadge';
import SoilingRisk from './SoilingRisk';
import SoilingRatio from './SoilingRatio';
import { getYearlyDaysFromDate } from '../../utils/utils';
import GlobalSoilingIndex from './GlobalSoilingIndex';
import CardBox from '../common/CardBox';
import { handleError } from '../../utils/Error';
import toast from 'react-hot-toast';
import { InfoAlert } from '../common/InfoAlert';
import { useFormData } from '../../hooks/useFormValues';
import type { SoilingAssessment } from '../../services/Server';

export const dynamicText = {
  a: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      It is advised to run the mitigation strategy process to evaluate cleaning solutions suitable for your project.
    </Typography>
  ),
  b: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Rain data indicates that you may experience more than 12 months without rainfall year (rain {'<'} 1.2 mm /day), which could lead to
      significant soiling accumulation.
    </Typography>
  ),
  c: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Relative humidity and wind speed data indicate that these weather conditions are conducive to rapid soiling accumulation. Therefore,
      soiling losses could be considerable during extended dry periods.
    </Typography>
  ),
  d: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Airborne particulate matter shows a high rate of particle accumulation. Therefore, soiling losses could be considerable during
      extended dry periods.
    </Typography>
  ),
  e: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Typical year indicates that there is never a month where total rain is below 30 mm/month, Preventing soiling accumulation.
    </Typography>
  ),
};

export const dynamicText0Switch = (data: SoilingAssessment) => {
  const text = [];
  if (data?.scores?.global > 0.5) {
    text.push(dynamicText['a']);
  }
  if (data?.scores?.rain >= 0.9) {
    text.push(dynamicText['b']);
  }
  if (data?.scores?.deposition > 0.45 && data?.scores?.global < 0.55) {
    text.push(dynamicText['c']);
  }
  if (data?.scores?.soiling_level > 3 && data?.scores?.global < 0.55) {
    text.push(dynamicText['d']);
  }
  if (data?.scores?.deposition === 0 || data?.scores?.rain < 0.3) {
    text.push(dynamicText['e']);
  }
  return text.length ? (
    <CardBox>
      {text.map((el, i) => (
        <Fragment key={i}>{el}</Fragment>
      ))}
    </CardBox>
  ) : (
    ''
  );
};

type Props = {
  name: string;
  projectId: string;
  setResults: (updater: (prevResults: Record<string, unknown>) => Record<string, unknown>) => void;
};

type ApiError = { response?: { data?: { detail?: unknown } } };

// A genuine assessment run takes seconds (weather fetch + soiling model); a
// sub-second response means the backend replayed a saved result. Used only to
// surface the "Saved result" badge — never to gate behaviour.
const CACHED_LATENCY_THRESHOLD_MS = 600;

const AppSoilingAssessment = ({ name, projectId, setResults }: Props) => {
  const { setModified } = useAppContext();
  const formData = useFormData();
  const { postSoilingAssessmentAPI } = Server();
  const [cached, setCached] = useState<boolean>(false);

  const {
    data: data = {} as SoilingAssessment,
    isLoading: isLoading,
    isError: isError,
    error: error,
  } = useQuery({
    queryKey: [
      'soiling-assessment-' +
        JSON.stringify({
          ...formData.location,
          yearly_cleaning_dates: getYearlyDaysFromDate(formData.yearly_cleaning_days),
          cleaning_efficiency: formData.cleaning_efficiency,
          variable_tilt_angle: formData?.variable_tilt_angle,
          ...formData?.tilt_angle,
          surface_tilt: formData.surface_tilt,
          rain_efficiency: formData.rain.rain_efficiency,
          rain_threshold: formData?.rain_threshold,
          user_patterns: {
            ...(formData?.rain?.from_api !== true && formData?.rain?.rain_pattern ? { rain_pattern: formData.rain.rain_pattern } : {}),
            ...(formData?.relative_humidity?.from_api !== true && formData?.relative_humidity?.pattern
              ? { relative_humidity_pattern: formData.relative_humidity.pattern }
              : {}),
            ...(formData?.wind_speed?.from_api !== true && formData?.wind_speed?.pattern
              ? { wind_speed_pattern: formData.wind_speed.pattern }
              : {}),
            ...(formData?.soiling_ratio?.from_api !== true && formData?.soiling_ratio?.pattern
              ? { soiling_ratio_pattern: formData.soiling_ratio.pattern }
              : {}),
          },
        }),
    ],
    queryFn: async () => {
      setCached(false);
      const start = performance.now();
      const res = await postSoilingAssessmentAPI({ project_id: projectId });
      setCached(performance.now() - start < CACHED_LATENCY_THRESHOLD_MS);
      return res;
    },
    gcTime: 0,
  });

  useEffect(() => {
    if (isError) {
      const detail = (error as ApiError | null)?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((elem: { msg: string; loc: string[] }) => toast.error(`${elem.msg}: ${elem.loc.join(' - ')}`));
      } else {
        handleError(error);
      }
    }
  }, [isError, error]);

  const lastGlobalIndex = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (data?.scores?.global == null) return;
    const globalIndex = Math.round(data.scores.global * 10000) / 100;
    if (lastGlobalIndex.current === globalIndex) return;
    lastGlobalIndex.current = globalIndex;
    setResults((prevResults) => ({ ...prevResults, global_soiling_index: globalIndex }));
    setModified(true);
  }, [data]);

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          Object.keys(data).length > 0 && (
            <>
              <InfoAlert severity="info">
                This page provides a comprehensive risk assessment for soiling buildup, based on the meteorological and soiling data defined
                in the previous steps.
              </InfoAlert>
              {cached && (
                <Box sx={{ mt: 2 }}>
                  <CachedResultBadge cached={cached} />
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <GlobalSoilingIndex data={data} />
              </Box>
              <Box>
                <SoilingRisk data={{ scores: data?.scores }} name={name} />
              </Box>
              <Box>
                <SoilingRatio
                  data={data?.soiling}
                  yearly_cleaning_days={formData?.yearly_cleaning_days}
                  rain_threshold={formData?.rain_threshold}
                  ymin={0.4}
                  ymax={1.2}
                />
              </Box>
            </>
          )
        )}
      </Box>
    </>
  );
};

export default AppSoilingAssessment;
